import { useQueryClient } from '@tanstack/react-query';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import {
  getListCandidatesQueryKey,
  useApproveCandidate,
  useListCandidates,
  useRejectCandidate,
} from '@/api/admin/admin';
import type { DishCandidateDto } from '@/api/model';
import { useGetMe } from '@/api/me/me';
import { Button } from '@/components/ui/button';
import { FilterChip } from '@/components/ui/chip';
import { Card, CardTitle } from '@/components/ui/card';

const STATUSES = [
  { value: 'pending', label: 'รอตรวจ' },
  { value: 'approved', label: 'อนุมัติแล้ว' },
  { value: 'rejected', label: 'ปฏิเสธ' },
] as const;

/**
 * Review queue for dishes the bot could not find (spec §7).
 *
 * Approving copies the candidate into the dish table, so the next person who
 * logs it gets a real match instead of another guess.
 */
export function Admin() {
  const me = useGetMe();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string>('pending');

  const candidates = useListCandidates({ status: status as 'pending' });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: getListCandidatesQueryKey() });
  };

  const approve = useApproveCandidate({ mutation: { onSuccess: refresh } });
  const reject = useRejectCandidate({ mutation: { onSuccess: refresh } });

  if (me.data && !me.data.isAdmin) {
    return (
      <Card>
        <p className="text-sm text-ink-muted">หน้านี้สำหรับผู้ดูแลระบบเท่านั้น</p>
      </Card>
    );
  }

  return (
    <Card className="space-y-3">
      <CardTitle>อาหารรอตรวจ</CardTitle>

      <div className="flex gap-2">
        {STATUSES.map((option) => (
          <FilterChip
            key={option.value}
            selected={option.value === status}
            onClick={() => setStatus(option.value)}
          >
            {option.label}
          </FilterChip>
        ))}
      </div>

      {candidates.isPending && <p className="text-sm text-ink-muted">กำลังโหลด…</p>}

      {candidates.data?.length === 0 && (
        <p className="text-sm text-ink-muted">ไม่มีรายการในสถานะนี้</p>
      )}

      <ul className="divide-y divide-border-subtle">
        {candidates.data?.map((candidate) => (
          <CandidateRow
            key={candidate.id}
            candidate={candidate}
            busy={
              (approve.isPending && approve.variables?.id === candidate.id) ||
              (reject.isPending && reject.variables?.id === candidate.id)
            }
            onApprove={() => approve.mutate({ id: candidate.id })}
            onReject={() => reject.mutate({ id: candidate.id })}
          />
        ))}
      </ul>
    </Card>
  );
}

function CandidateRow({
  candidate,
  busy,
  onApprove,
  onReject,
}: {
  candidate: DishCandidateDto;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  // Without macros there is nothing to promote; the row is shown so the gap is
  // visible, but approving it would fail.
  const priceable = candidate.kcalPer100g != null && candidate.servingGrams != null;
  const perServing =
    priceable && candidate.kcalPer100g != null && candidate.servingGrams != null
      ? Math.round((candidate.kcalPer100g * candidate.servingGrams) / 100)
      : null;

  return (
    <li className="flex items-center gap-3 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{candidate.displayName}</p>
        <p className="text-xs text-ink-muted">
          ถูกถาม {candidate.occurrences} ครั้ง
          {perServing != null && ` · ~${perServing} kcal/${candidate.servingLabel ?? 'ที่'}`}
          {candidate.estimated && ' · ค่าประมาณจาก AI'}
        </p>
        {!priceable && <p className="text-xs text-warning">ยังไม่มีตัวเลข — อนุมัติไม่ได้</p>}
      </div>

      {candidate.status === 'pending' && (
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={onApprove}
            disabled={busy || !priceable}
            aria-label={`อนุมัติ ${candidate.displayName}`}
          >
            <Check className="size-4" aria-hidden />
          </Button>
          <Button
            variant="danger"
            size="icon"
            onClick={onReject}
            disabled={busy}
            aria-label={`ปฏิเสธ ${candidate.displayName}`}
          >
            <X className="size-4" aria-hidden />
          </Button>
        </>
      )}
    </li>
  );
}
